import { useEffect } from 'react';

const SITE_NAME = 'static.noise';
const DEFAULT_DESCRIPTION =
    'Collages stitched from second-hand books, flea-market photos and diaries found in old basements — with the source material and the notes that came with it.';

interface DocumentMeta {
    /** Page title, without the site-name suffix. Omit on the homepage. */
    title?: string;
    description?: string;
    /** Absolute or root-relative path to the sharing image. */
    image?: string;
    type?: 'website' | 'article';
}

const setTag = (attr: 'name' | 'property', key: string, value: string) => {
    let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute('content', value);
};

const absolute = (path: string) =>
    path.startsWith('http') ? path : `${window.location.origin}${path}`;

/**
 * Sets the document title and the sharing metadata for the current route.
 *
 * Every page previously shared one hardcoded `<title>static-noise</title>`
 * with no description and no Open Graph tags, so pasting a collage link
 * into Instagram, WhatsApp or Twitter produced a bare URL with no preview.
 * For a visual portfolio that is the main way the work travels.
 */
const useDocumentMeta = ({
    title,
    description = DEFAULT_DESCRIPTION,
    image,
    type = 'website'
}: DocumentMeta) => {
    useEffect(() => {
        const fullTitle = title ? `${title} — ${SITE_NAME}` : SITE_NAME;
        document.title = fullTitle;

        const url = window.location.href;
        const shareImage = absolute(image || '/images/thumbnails/collages/touch_grass.webp');

        setTag('name', 'description', description);

        setTag('property', 'og:site_name', SITE_NAME);
        setTag('property', 'og:title', fullTitle);
        setTag('property', 'og:description', description);
        setTag('property', 'og:type', type);
        setTag('property', 'og:url', url);
        setTag('property', 'og:image', shareImage);

        setTag('name', 'twitter:card', 'summary_large_image');
        setTag('name', 'twitter:title', fullTitle);
        setTag('name', 'twitter:description', description);
        setTag('name', 'twitter:image', shareImage);
    }, [title, description, image, type]);
};

export default useDocumentMeta;
